import React, { FC, memo, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { GeolocationCoords } from 'app/types/common';
import { YandexMarker, YandexMapOptions } from '../model/types';
import { YANDEX_MAPS_DEFAULT_ZOOM, YANDEX_MAPS_DEFAULT_CONTROLS } from '../lib/constants';
import { useYandexMapsApi } from '../lib/hooks/useYandexMapsApi';
import { YandexMapMarker } from './YandexMapMarker';
import { useGetCurrencySymbol, getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';

interface YandexMapProps extends PropsWithChildren {
  mapId: string | undefined;
  center: Nullable<GeolocationCoords>;
  selectedMarkerId?: Nullable<string>;
  selectMarker?: (latitude: number, longitude: number, markerId: string) => void;
  zoom?: number;
  markers?: YandexMarker[];
  withMarkerPopups?: boolean;
  routeResult?: Nullable<unknown>;
  mapContainerClassName?: string;
  onSelectMarker?: (markerCoords: GeolocationCoords) => void;
  onSelectWarehouseAndProceed?: (markerId: string) => void;
  isMarkerClickable?: boolean;
}

export const YandexMap: FC<YandexMapProps> = memo((props) => {
  const {
    mapId,
    children,
    center,
    selectMarker,
    selectedMarkerId,
    zoom = YANDEX_MAPS_DEFAULT_ZOOM,
    markers,
    routeResult,
    onSelectMarker,
    withMarkerPopups,
    mapContainerClassName,
    onSelectWarehouseAndProceed,
    isMarkerClickable = true,
  } = props;

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const [map, setMap] = useState<unknown>(null);
  const { isLoaded, error, ymaps } = useYandexMapsApi();

  const currencySymbol = useGetCurrencySymbol();
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);

  // Initialize map when API is loaded
  useEffect(() => {
    if (!isLoaded || !ymaps || !mapRef.current) return;

    // Check if Map constructor is available
    if (!window.ymaps || !('Map' in window.ymaps)) {
      console.error('Yandex Maps Map constructor not available');
      return;
    }

    // If we already have a map instance, don't create a new one
    if (mapInstanceRef.current) {
      setMap(mapInstanceRef.current);
      return;
    }

    try {
      const defaultCenter: [number, number] = [55.7558, 37.6176]; // Default to Moscow
      const mapCenter: [number, number] = center ? [center.lat, center.lng] : defaultCenter;

      const mapOptions: YandexMapOptions = {
        center: mapCenter,
        zoom,
        controls: YANDEX_MAPS_DEFAULT_CONTROLS,
      };

      const yandexMap = new (window.ymaps as any).Map(mapRef.current, mapOptions);
      mapInstanceRef.current = yandexMap;
      setMap(yandexMap);
    } catch (err) {
      console.error('Error creating Yandex Map:', err);
    }
  }, [isLoaded, ymaps, center, zoom]);

  // Cleanup map instance when component unmounts
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current && typeof (mapInstanceRef.current as any).destroy === 'function') {
        try {
          (mapInstanceRef.current as any).destroy();
          mapInstanceRef.current = null;
        } catch (err) {
          console.error('Error destroying Yandex Map:', err);
        }
      }
    };
  }, []);

  // Update map center when center prop changes
  useEffect(() => {
    if (map && center && typeof (map as any).setCenter === 'function') {
      try {
        (map as any).setCenter([center.lat, center.lng]);
      } catch (err) {
        console.error('Error setting map center:', err);
      }
    }
  }, [map, center]);

  // Update zoom when selected marker changes
  useEffect(() => {
    if (map && selectedMarkerId && typeof (map as any).setZoom === 'function') {
      try {
        (map as any).setZoom(16);
      } catch (err) {
        console.error('Error setting map zoom:', err);
      }
    }
  }, [map, selectedMarkerId]);

  // Handle route display
  useEffect(() => {
    if (!map || !routeResult) return;

    try {
      // Clear existing routes
      if (typeof (map as any).geoObjects?.removeAll === 'function') {
        (map as any).geoObjects.removeAll();
      }

      // Add new route
      if (routeResult && typeof (map as any).geoObjects?.add === 'function') {
        (map as any).geoObjects.add((routeResult as any).route);
      }
    } catch (err) {
      console.error('Error handling route display:', err);
    }
  }, [map, routeResult]);

  if (error) {
    return <div className="text-red-500">Error loading Yandex Maps: {error}</div>;
  }

  if (!isLoaded) {
    return <div className="flex items-center justify-center h-full">Loading Yandex Maps...</div>;
  }

  return (
    <div className={mapContainerClassName} ref={mapRef}>
      {markers?.map((marker, index) => (
        <YandexMapMarker
          key={index}
          map={map}
          markerData={marker}
          selectedMarkerId={selectedMarkerId}
          selectMarker={selectMarker}
          onSelectMarker={onSelectMarker}
          withMarkerPopups={withMarkerPopups}
          currencySymbol={currencySymbol}
          showPriceWithVat={globalSettings?.showPriceWithVat || false}
          loggedUserAccountType={loggedUser?.accountType}
          onSelectWarehouseAndProceed={onSelectWarehouseAndProceed}
          isClickable={isMarkerClickable}
        />
      ))}
      {children}
    </div>
  );
});
